import {
  Box,
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import {
  useCrashConditionWet,
  useCrashConditionNight,
  useCrashConditionIntersection,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Crash Conditions (per 100,000 Population)',
  content: [
    {
      title: 'DSI in wet conditions',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population that occurred in wet conditions including snow.
  </Text>,
      source: null,
    },
    {
      title: 'DSI at night',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population that occurred at night (outside the hours of twilight).
  </Text>,
      source: null,
    },
    {
      title: 'DSI at intersections',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population that occurred at intersections between roads.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Crash data sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/partners/data-and-tools/crash-analysis-system" isExternal>NZTA Crash Analysis System.</Link>
    {' '}
    Population sourced from Statistics New Zealand
    {' '}
    <Link href="https://www.stats.govt.nz/information-releases/subnational-population-estimates-at-30-june-2023/" isExternal>Subnational Population Estimates.</Link>
    {' '}
    Road Efficiency Group Te Ringa Maimoa has calculated these results from these data sources.
  </Text>,
    },
  ],
};

function RoadSafetyCrashConditions() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: crashConditionWetData, isLoading: isLoadingCrashConditionWet } = useCrashConditionWet();
  const { data: crashConditionNightData, isLoading: isLoadingCrashConditionNight } = useCrashConditionNight();
  const { data: crashConditionIntersectionData, isLoading: isLoadingCrashConditionIntersection } = useCrashConditionIntersection();
  const isLoading = isLoadingCrashConditionWet || isLoadingCrashConditionNight || isLoadingCrashConditionIntersection;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Check if all lockYears are the same
  const allLockYears = [
    crashConditionWetData?.[0]?.LockYear,
    crashConditionNightData?.[0]?.LockYear,
    crashConditionIntersectionData?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // Wet Conditions Chart
  const wetConditionsYears = [...new Set(crashConditionWetData?.map((item) => item.Year))];
  const wetConditionsData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: crashConditionWetData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: crashConditionWetData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: crashConditionWetData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const wetConditionsChartSeries = filterChartDataByName(
    wetConditionsData,
    chartFilters,
  );

  // Night Conditions Chart
  const nightConditionsYears = [...new Set(crashConditionNightData?.map((item) => item.Year))];
  const nightConditionsData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: crashConditionNightData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: crashConditionNightData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: crashConditionNightData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const nightConditionsChartSeries = filterChartDataByName(
    nightConditionsData,
    chartFilters,
  );

  // Intersection Conditions Chart
  const intersectionConditionsYears = [...new Set(crashConditionIntersectionData?.map((item) => item.Year))];
  const intersectionConditionsData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: crashConditionIntersectionData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: crashConditionIntersectionData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: crashConditionIntersectionData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const intersectionConditionsChartSeries = filterChartDataByName(
    intersectionConditionsData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Crash Conditions (per 100,000 Population)"
      informationSource={informationSource}
      isLoading={isLoading || !crashConditionWetData || !crashConditionNightData || !crashConditionIntersectionData}
      lockYear={primaryLockYear || null}
      links={[
        {
          label: 'ONF Safe Travel - Wet Roads / Night / Intersections',
          url: '/onf/transport-outcomes/hsp3',
          isExternal: false,
          userRoles: null,
        },
      ]}
    >
      <Wrap spacing={0}>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={crashConditionWetData?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="DSI in wet conditions"
            categories={wetConditionsYears}
            series={wetConditionsChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={1}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={crashConditionNightData?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="DSI at night"
            categories={nightConditionsYears}
            series={nightConditionsChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={1}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={crashConditionIntersectionData?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="DSI at intersections"
            categories={intersectionConditionsYears}
            series={intersectionConditionsChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={1}
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default RoadSafetyCrashConditions;
